// Registro de piezas. Cada definición describe lo que comparten la vista 3D y el simulador:
// nombre, categoría, propiedades, pines (posición local en mm) y conexiones internas.
// Aquí no se importa three.js: este módulo también se carga en el Web Worker.
//
// Convención de coordenadas (mm): X a la derecha, Y hacia arriba, Z hacia el frente
// (en la vista superior, -Z queda arriba de la pantalla). y = 0 es la base de la pieza.

export const PITCH = 2.54;

export const CATEGORIES = [
  { id: 'placas', name: 'Placas' },
  { id: 'prototipado', name: 'Prototipado' },
  { id: 'basicos', name: 'Básicos' },
  { id: 'luces', name: 'Luces y pantallas' },
  { id: 'entradas', name: 'Entradas' },
  { id: 'sensores', name: 'Sensores' },
  { id: 'actuadores', name: 'Actuadores y sonido' },
  { id: 'logica', name: 'Lógica y potencia' },
  { id: 'comunicacion', name: 'Comunicación' },
  { id: 'instrumentos', name: 'Instrumentos' },
];

export const PARTS = {};

export function definePart(def) {
  if (PARTS[def.type]) throw new Error('Pieza duplicada: ' + def.type);
  def.props = def.props || [];
  def.sim = def.sim || def.type;
  PARTS[def.type] = def;
  return def;
}

export function getDef(type) {
  const def = PARTS[type];
  if (!def) throw new Error('Pieza desconocida: ' + type);
  return def;
}

/** Propiedades completas de una pieza (valores guardados + valores por omisión). */
export function resolveProps(type, props = {}) {
  const def = getDef(type);
  const out = {};
  for (const p of def.props) out[p.key] = props[p.key] !== undefined ? props[p.key] : p.default;
  for (const k of Object.keys(props)) if (out[k] === undefined) out[k] = props[k];
  return out;
}

const pinCache = new WeakMap();

/** Lista de pines de una pieza para unas propiedades dadas (con caché por objeto de props). */
export function getPins(type, props) {
  const def = getDef(type);
  if (typeof def.pins !== 'function') return def.pins;
  let byDef = pinCache.get(props);
  if (!byDef) { byDef = new Map(); pinCache.set(props, byDef); }
  let pins = byDef.get(def);
  if (!pins) { pins = def.pins(props); byDef.set(def, pins); }
  return pins;
}

export function getBuses(type, props) {
  const def = getDef(type);
  if (!def.buses) return [];
  return typeof def.buses === 'function' ? def.buses(props) : def.buses;
}

/** Huella de la pieza en el plano XZ (mm), centrada en su origen salvo que indique offset. */
export function getFootprint(type, props) {
  const def = getDef(type);
  const f = typeof def.footprint === 'function' ? def.footprint(props) : def.footprint;
  return { w: 10, d: 10, h: 10, x: 0, z: 0, ...f };
}

// Utilidades para declarar pines en fila con paso de 0.1".
export function pinRow(names, { x0 = 0, z = 0, y = 0, dx = PITCH, insert = true, labels = {}, kinds = {} } = {}) {
  return names.map((name, i) => ({
    name,
    label: labels[name] || name,
    kind: kinds[name] || guessKind(name),
    x: x0 + i * dx,
    y,
    z,
    insert,
  }));
}

/** Fila de pines centrada en x = 0. */
export function centeredRow(names, opts = {}) {
  const dx = opts.dx || PITCH;
  return pinRow(names, { ...opts, x0: (opts.x0 || 0) - ((names.length - 1) * dx) / 2 });
}

export function guessKind(name) {
  const n = String(name).toUpperCase();
  if (/^(GND|VSS|K|COM-|−|-|V-|BN|TN|NEG)/.test(n) || n.startsWith('GND')) return 'gnd';
  if (/^(VCC|VDD|5V|3\.3V|3V3|VIN|V\+|\+|IOREF|A$|VMOT|VBUS|POS)/.test(n)) return 'power';
  return 'signal';
}
